/**
 * @fileoverview Module for `src/dashboard/voter-adoption/VoterAdoptionButton.js`.
 */

import t from '../../lib/deprecated_i18n';
import { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Action button that submits an adoption request for a configured voter quantity.
 *
 * @param {Object} params
 * @returns {JSX.Element} Adoption action button with contextual messaging.
 */
function VoterAdoptionButton({
  voterQuantity,
  subtitleMessage,
  voterAdoptionMutation,
  liveVoterCount,
  isAdopterMaxedOut,
  campaign,
  isCampaignClosed,
  socialBatchQuantity = 0,
  adoptionType = 'individual',
}) {
  const [mostRecentRequestedAmount, setMostRecentRequestedAmount] = useState(0);

  /**
   * Submits a bundle adoption request and briefly tracks the requested amount.
   *
   * @returns {void}
   */
  function adoptVoter() {
    setMostRecentRequestedAmount(voterQuantity);
    voterAdoptionMutation.mutate({
      bundleSize: Number(voterQuantity),
      batchSize: Number(socialBatchQuantity),
      districtId: campaign.district_id,
      type: 'BUNDLE',
    });

    // Only show the 'success' message for a short time, if at all.
    setTimeout(() => {
      setMostRecentRequestedAmount(0);
    }, 5000);
  }

  /**
   * Builds the button label based on mutation state and campaign context.
   *
   * @param {number} lettersPerBundle
   * @param {number} bundlesPerBatch
   * @returns {JSX.Element} Button content.
   */
  function getAdoptButtonMessageJsx(lettersPerBundle, bundlesPerBatch) {
    if (voterAdoptionMutation.isSuccess && !isCampaignClosed && mostRecentRequestedAmount === voterQuantity) {
      return (<div>{ t('adopt.button.success') } <i className='fa fa-check' /></div>);
    }
    let messageKey;
    if (adoptionType === 'party') {
      messageKey = `adopt.button.social.message${ socialBatchQuantity === 1 ? '.singular' : '' }`;
    } else if (campaign.state_abbr === 'US') {
      // 'US' is not a state. It marks a nationwide campaign (e.g. GTCA_2026_C4_ALL),
      // which admins express by typing 'US' into the free-text districts.state_abbr
      // column. Omit the location rather than falling through to the generic strings,
      // which interpolate {{stateAbbr}} and would render "Adopt 5 US Voters".
      messageKey = liveVoterCount > 1 ? 'adopt.button.noState.more.message' : 'adopt.button.noState.initial.message';
    } else {
      messageKey = liveVoterCount > 1 ? 'adopt.button.more.message' : 'adopt.button.initial.message';
    }
    let message = t(messageKey);
    message = message
      .replace('{{count}}', lettersPerBundle)
      .replace('{{bundleCount}}', bundlesPerBatch)
      .replace('{{stateAbbr}}', campaign.state_abbr);
    return (<div>{ message }</div>);
  }

  const disableButtons = voterAdoptionMutation.isLoading || isAdopterMaxedOut || isCampaignClosed;

  return (
    <div className='col-md'>
      <button
        className={ `btn btn-primary w-100 mt-1 ${ adoptionType }` }
        disabled={ disableButtons }
        onClick={ adoptVoter }
      >
        { getAdoptButtonMessageJsx(voterQuantity, socialBatchQuantity) }
      </button>
      <div className='small mt-1 text-center fw-bold text-gray'>
        { subtitleMessage }
      </div>
    </div>
  );
}

VoterAdoptionButton.propTypes = {
  adoptionType: PropTypes.string,
  campaign: PropTypes.object,
  isAdopterMaxedOut: PropTypes.bool,
  isCampaignClosed: PropTypes.bool,
  liveVoterCount: PropTypes.number,
  socialBatchQuantity: PropTypes.number,
  subtitleMessage: PropTypes.string,
  voterAdoptionMutation: PropTypes.object,
  voterQuantity: PropTypes.number,
};

export default VoterAdoptionButton;
